import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  HandHeart,
  HousePlus,
  Stethoscope,
  ClipboardPlus,
  Users,
  HeartHandshake,
  ArrowRight,
  ShieldCheck,
  Clock3,
  BadgeCheck,
  Star,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, LineReveal, FadeIn, GoldRule, Overline } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?q=85&w=1800&auto=format&fit=crop";
const HANDS_IMG =
  "https://images.unsplash.com/photo-1682352689072-7b2c0b8580c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const TEAM_IMG =
  "https://images.pexels.com/photos/6129502/pexels-photo-6129502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200";

const services = [
  {
    icon: HandHeart,
    title: "Personal Care Assistance",
    text: "Dignified help with bathing, grooming, dressing, and mobility — delivered with patience and respect.",
  },
  {
    icon: HousePlus,
    title: "Home Health Aide Services",
    text: "Certified aides supporting daily living: meals, light housekeeping, errands, and appointment escorts.",
  },
  {
    icon: Stethoscope,
    title: "Skilled Nursing",
    text: "RN and LPN care at home — medication management, wound care, and chronic condition monitoring.",
  },
  {
    icon: ClipboardPlus,
    title: "Private Duty Nursing",
    text: "Extended one-on-one clinical care for complex medical needs, recovery, and ongoing support.",
  },
  {
    icon: Users,
    title: "Companion & Support Services",
    text: "Warm companionship, conversation, activities, and transportation that brighten everyday life.",
  },
  {
    icon: HeartHandshake,
    title: "Family & Caregiver Support",
    text: "Respite care, care coordination, and guidance that gives families confidence and breathing room.",
  },
];

const chapters = [
  {
    num: "01",
    title: "Dignity in Independence",
    text: "We believe home is where healing happens best. Every care plan is built to protect independence, honor routines, and preserve the dignity of the people we serve.",
  },
  {
    num: "02",
    title: "Skilled RN Supervision",
    text: "Every caregiver on our team works under the oversight of registered nurses — so families can trust that care is not only kind, but clinically sound.",
  },
  {
    num: "03",
    title: "Customized Care Plans",
    text: "No two families are alike. We listen first, then design a flexible plan around your loved one's needs, preferences, and goals — and adjust as life changes.",
  },
  {
    num: "04",
    title: "24/7 Family Support",
    text: "Questions don't keep office hours, and neither do we. Our care team is available around the clock so you are never navigating care alone.",
  },
];

const stats = [
  { icon: BadgeCheck, value: "500+", label: "Families served across NJ" },
  { icon: Clock3, value: "24/7", label: "On-call care team support" },
  { icon: ShieldCheck, value: "100%", label: "Background-checked caregivers" },
  { icon: Star, value: "4.9", label: "Average family satisfaction" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <main>
      {/* ---------- HERO ---------- */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex min-h-[100svh] items-center overflow-hidden"
      >
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Professional caregiver warmly assisting a client at home"
            className="h-[115%] w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-blush-50/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/50" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8">
          <FadeIn delay={0.1}>
            <Overline>New Jersey Home Healthcare</Overline>
          </FadeIn>
          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-charcoal sm:text-6xl lg:text-7xl"
          >
            <LineReveal delay={0.25}>Compassionate Care.</LineReveal>
            <LineReveal delay={0.42}>
              Right Where You <em className="text-gold-deep">Belong.</em>
            </LineReveal>
          </h1>
          <FadeIn delay={0.7} className="mt-7 max-w-xl">
            <p className="text-base leading-relaxed text-charcoal-muted sm:text-lg">
              Supporting individuals and families through dependable,
              personalized home healthcare throughout New Jersey.
            </p>
          </FadeIn>
          <FadeIn delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/request-care" data-testid="hero-cta-request-care" className="btn-pink">
              Request Care <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid="hero-cta-our-services" className="btn-gold-outline">
              Our Services
            </Link>
          </FadeIn>
        </div>

        <FadeIn
          delay={1.4}
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 sm:block"
        >
          <div className="flex flex-col items-center gap-2 text-charcoal-faint">
            <span className="text-[0.6rem] uppercase tracking-[0.35em]">Scroll</span>
            <span className="h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
          </div>
        </FadeIn>
      </section>

      <Marquee />

      {/* ---------- MANIFESTO CHAPTERS ---------- */}
      <section data-testid="manifesto-section" className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Overline>Our Care Philosophy</Overline>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              A promise written in four chapters
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-x-14 gap-y-14 md:grid-cols-2">
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.1} className="group">
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-5xl font-light italic text-gold/80 transition-colors duration-500 group-hover:text-gold sm:text-6xl">
                    {c.num}
                  </span>
                  <span className="h-px flex-1 bg-gradient-to-r from-gold/40 to-transparent" />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-charcoal">{c.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-charcoal-muted sm:text-base">
                  {c.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SERVICES GRID ---------- */}
      <section data-testid="services-section" className="bg-blush-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-col items-center text-center">
            <Overline centered>What We Do</Overline>
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Care that meets you where you are
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-charcoal-muted sm:text-base">
              From a few hours of companionship to around-the-clock skilled
              nursing, every service is delivered with warmth and clinical
              excellence.
            </p>
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.1}>
                <Link
                  to="/services"
                  data-testid="service-card-item"
                  className="group flex h-full flex-col rounded-3xl border border-blush-200/80 bg-white p-8 shadow-[0_2px_20px_rgba(31,41,55,0.04)] transition-[transform,box-shadow,border-color,background-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_18px_44px_rgba(232,180,188,0.28)]"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft transition-colors duration-300 group-hover:bg-blush-100">
                    <s.icon className="h-6 w-6 text-gold" strokeWidth={1.4} />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-charcoal">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal-muted">
                    {s.text}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gold-deep">
                    Learn more
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHY CHOOSE US ---------- */}
      <section data-testid="why-choose-section" className="bg-white py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-[2rem] border border-gold/40" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_24px_60px_rgba(31,41,55,0.12)]">
              <img
                src={HANDS_IMG}
                alt="Caregiver gently holding a senior client's hands"
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blush-200/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-4 animate-float-slow rounded-2xl border border-gold/30 bg-white/95 px-6 py-4 shadow-xl backdrop-blur sm:-right-8">
              <p className="font-serif text-3xl text-gold-deep">15+</p>
              <p className="text-xs uppercase tracking-[0.18em] text-charcoal-muted">
                Years of trusted care
              </p>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Overline>Why New Beginnings</Overline>
              <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Care you can trust, from people who truly care
              </h2>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-charcoal-muted sm:text-base">
                Choosing care for someone you love is one of life's most
                important decisions. We earn that trust every day — through
                rigorous caregiver screening, registered nurse oversight, and a
                genuine commitment to treating every client like family.
              </p>
            </Reveal>
            <div className="mt-12 grid grid-cols-2 gap-8">
              {stats.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08}>
                  <div className="flex items-center gap-3">
                    <s.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                    <span className="font-serif text-3xl text-charcoal sm:text-4xl">
                      {s.value}
                    </span>
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-[0.14em] text-charcoal-muted">
                    {s.label}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      </div>

      {/* ---------- CAREERS TEASER ---------- */}
      <section data-testid="careers-teaser-section" className="bg-blush-50 py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <Reveal>
              <Overline>Careers at New Beginnings</Overline>
              <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Join Our Caring Team
              </h2>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-charcoal-muted sm:text-base">
                We're always looking for compassionate nurses, home health
                aides, and caregivers who believe care is a calling — not just
                a job. Grow your career where your work truly matters.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-9 flex flex-wrap gap-4">
              <Link to="/careers" data-testid="careers-apply-now-button" className="btn-pink">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/careers" className="btn-gold-outline">
                View Open Roles
              </Link>
            </Reveal>
            <Reveal delay={0.25} className="mt-10">
              <GoldRule />
            </Reveal>
          </div>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] bg-blush-200" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[2rem] border border-gold/25 shadow-[0_24px_60px_rgba(31,41,55,0.12)]">
                <img
                  src={TEAM_IMG}
                  alt="Diverse team of healthcare professionals smiling together"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- REQUEST CARE CTA ---------- */}
      <section data-testid="request-care-cta-section" className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="gold-frame px-6 py-16 text-center sm:px-16 sm:py-20">
              <span className="absolute -top-px left-1/2 h-px w-24 -translate-x-1/2 bg-gold" aria-hidden="true" />
              <span className="absolute -bottom-px left-1/2 h-px w-24 -translate-x-1/2 bg-gold" aria-hidden="true" />
              <GoldRule className="justify-center" />
              <h2 className="mt-6 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Ready for a New Beginning?
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-charcoal-muted sm:text-base">
                Our compassionate team is here to help you understand your
                options and find the care that's right for you or your loved
                one.
              </p>
              <Link
                to="/request-care"
                data-testid="cta-request-consultation-button"
                className="btn-pink mt-9"
              >
                Request a Care Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal-faint">
                Free consultation &middot; No obligation &middot; Response within 24 hours
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
